import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

// Forma canónica de cliente que consumen las páginas (coincide con el mock).
export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  documentType: string;
  documentNumber: string;
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
  city: string;
  department: string;
  registrationDate: string;
  totalPurchases: number;
  purchaseCount: number;
  averageTicket: number;
  lastPurchase: string | null;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  originChannel: string;
  tags: string[];
  notes: string;
  isActive: boolean;
}

// Fila real de la tabla `customers` en Supabase.
interface DbCustomer {
  id: string;
  first_name: string | null;
  last_name: string | null;
  document_id: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  department: string | null;
  notes: string | null;
  created_at: string | null;
  is_active: boolean | null;
}

export interface CustomersResult {
  data: Customer[];
}

// Mapea una fila de `customers` a la forma canónica.
// El esquema real guarda solo datos maestros de contacto (sin analítica de
// compras ni atribución UTM, que hoy solo vive en el mock). Se completan con
// defaults seguros para que la UI no rompa.
function mapDbCustomer(row: DbCustomer): Customer {
  return {
    id: row.id,
    firstName: row.first_name ?? '',
    lastName: row.last_name ?? '',
    documentType: 'CC',
    documentNumber: row.document_id ?? '',
    phone: row.phone ?? '',
    whatsapp: row.whatsapp ?? '',
    email: row.email ?? '',
    address: row.address ?? '',
    city: row.city ?? '',
    department: row.department ?? '',
    registrationDate: row.created_at ? row.created_at.split('T')[0] : '',
    totalPurchases: 0,
    purchaseCount: 0,
    averageTicket: 0,
    lastPurchase: null,
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmContent: '',
    utmTerm: '',
    originChannel: '',
    tags: [],
    notes: row.notes ?? '',
    isActive: row.is_active ?? true,
  };
}

// Consulta a `customers` con fallback automático al mock:
//   - sin credenciales -> mock
//   - error de conexión/consulta -> mock
//   - sin sesión autenticada (RLS filtra) -> mock
//   - tabla vacía -> mock
export async function fetchCustomers(): Promise<CustomersResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    // Consulta acumulativa: clientes + pedidos + ventas, para calcular métricas
    // reales de compra en lugar de mostrar siempre $0.
    const [customersRes, ordersRes, salesRes] = await Promise.all([
      supabase.from('customers').select('*'),
      supabase.from('orders').select('customer_id, total, status, created_at').neq('status', 'cancelled'),
      supabase.from('sales').select('customer_id, order_id, total, sale_date'),
    ]);

    if (customersRes.error) throw new Error(customersRes.error.message);
    if (ordersRes.error) throw new Error(ordersRes.error.message);
    if (salesRes.error) throw new Error(salesRes.error.message);

    const rows = (customersRes.data ?? []) as DbCustomer[];

    // Agregación por cliente: pedidos (no cancelados) + ventas directas sin pedido.
    const agg = new Map<string, { total: number; count: number; last: string }>();

    const accumulate = (customerId: string, amount: number, date: string) => {
      const cur = agg.get(customerId) ?? { total: 0, count: 0, last: '' };
      cur.total += amount;
      cur.count += 1;
      if (date && (!cur.last || date > cur.last)) cur.last = date;
      agg.set(customerId, cur);
    };

    ((ordersRes.data ?? []) as Array<Record<string, any>>).forEach((o) => {
      accumulate(o.customer_id, Number(o.total ?? 0), o.created_at ?? '');
    });

    // Ventas directas (sin order_id) para no duplicar el monto de pedidos ya
    // convertidos en venta. Las ventas ligadas a un pedido ya se contaron arriba.
    ((salesRes.data ?? []) as Array<Record<string, any>>).forEach((s) => {
      if (s.order_id) return;
      accumulate(s.customer_id, Number(s.total ?? 0), s.sale_date ?? '');
    });

    const data = rows.map((row) => {
      const base = mapDbCustomer(row);
      const metrics = agg.get(row.id);
      if (metrics) {
        base.totalPurchases = metrics.total;
        base.purchaseCount = metrics.count;
        base.averageTicket = metrics.count > 0 ? metrics.total / metrics.count : 0;
        base.lastPurchase = metrics.last || null;
      }
      return base;
    });

    return { data };
  } catch (err) {
    console.error('[customers] Supabase connection failed:', err);
    throw err;
  }
}

// Entrada del formulario de cliente (crear).
export interface CustomerInput {
  firstName: string;
  lastName: string;
  documentId: string;
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
  city: string;
  department: string;
  notes: string;
}

// Crea un cliente nuevo en Supabase. Devuelve el cliente creado o un error
// legible para mostrar en el formulario.
export async function saveCustomer(
  input: CustomerInput,
): Promise<{ data: Customer | null; error: string | null }> {
  if (!(await canWrite('customers'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    first_name: input.firstName.trim(),
    last_name: input.lastName.trim(),
    document_id: input.documentId.trim() || null,
    phone: input.phone.trim(),
    whatsapp: input.whatsapp.trim() || null,
    email: input.email.trim() || null,
    address: input.address.trim(),
    city: input.city.trim() || 'Bogotá',
    department: input.department.trim() || 'Cundinamarca',
    notes: input.notes.trim() || null,
  };

  try {
    const { data, error } = await supabase.from('customers').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as DbCustomer | undefined;
    if (!row) return { data: null, error: 'No se pudo leer el cliente guardado.' };
    recordAudit({
      entityType: 'customers',
      entityId: row.id,
      action: 'CREATE',
      newValue: `${input.firstName} ${input.lastName}`.trim(),
    });
    return { data: mapDbCustomer(row), error: null };
  } catch (err) {
    console.warn('[customers] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar el cliente.' };
  }
}

// Actualiza los datos de contacto de un cliente existente. No se permite borrar
// clientes porque están referenciados por pedidos/ventas/garantías/cambios.
export async function updateCustomer(
  id: string,
  input: CustomerInput,
): Promise<{ data: Customer | null; error: string | null }> {
  if (!(await canWrite('customers'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    first_name: input.firstName.trim(),
    last_name: input.lastName.trim(),
    document_id: input.documentId.trim() || null,
    phone: input.phone.trim(),
    whatsapp: input.whatsapp.trim() || null,
    email: input.email.trim() || null,
    address: input.address.trim(),
    city: input.city.trim() || 'Bogotá',
    department: input.department.trim() || 'Cundinamarca',
    notes: input.notes.trim() || null,
  };

  try {
    const { data, error } = await supabase
      .from('customers')
      .update(payload)
      .eq('id', id)
      .select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as DbCustomer | undefined;
    if (!row) return { data: null, error: 'No se pudo leer el cliente actualizado.' };
    recordAudit({
      entityType: 'customers',
      entityId: id,
      action: 'UPDATE',
      fieldName: 'contact',
      newValue: `${input.firstName} ${input.lastName}`.trim(),
    });
    return { data: mapDbCustomer(row), error: null };
  } catch (err) {
    console.warn('[customers] update failed:', err);
    return { data: null, error: 'Error de conexión al actualizar el cliente.' };
  }
}

// Activa o desactiva (archiva) un cliente. Es un soft-delete: el registro se
// conserva porque está referenciado por pedidos/ventas/garantías/cambios.
export async function toggleCustomerActive(
  id: string,
  isActive: boolean,
): Promise<{ error: string | null }> {
  if (!(await canWrite('customers'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  try {
    const { error } = await supabase
      .from('customers')
      .update({ is_active: isActive })
      .eq('id', id);
    if (!error) {
      recordAudit({
        entityType: 'customers',
        entityId: id,
        action: 'UPDATE',
        fieldName: 'is_active',
        oldValue: String(!isActive),
        newValue: String(isActive),
      });
    }
    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[customers] toggle failed:', err);
    return { error: 'Error de conexión al actualizar el cliente.' };
  }
}